import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs } from '@/shared/components/ui';
import type { TabItem } from '@/shared/components/ui';
import { Button } from '@/shared/components/ui/Button';
import { Icon } from '@/shared/icons';
import { useCredits } from '../hooks';
import { CreditsDashboard } from '../components/CreditsDashboard';
import { CardsManager } from '../components/CardsManager';
import { InvoicesPage } from '../components/InvoicesPage';
import { AlertsPage } from '../components/AlertsPage';
import { ConsumptionStatement } from '../components/ConsumptionStatement';

/**
 * Pagina principal de Cobranca / Faturamento.
 *
 * Shell no Design System: toolbar de 3 zonas (breadcrumb -> acao primaria
 * "Mudar de plano") + tabbar sticky. Cada aba (Visão Geral, Extrato, Cartoes,
 * Faturas, Alertas) preserva integralmente seus widgets/graficos/acoes.
 */
export const BillingPage: React.FC = () => {
  const navigate = useNavigate();
  const { dashboard, isLoading } = useCredits();

  // Tab content: Overview
  const overviewContent = (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-8">
      <CreditsDashboard dashboard={dashboard} isLoading={isLoading} />
    </div>
  );

  // Tab content: Statement
  const statementContent = (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-8">
      <ConsumptionStatement />
    </div>
  );

  // Tab content: Cards
  const cardsContent = (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-8">
      <CardsManager />
    </div>
  );

  // Tab content: Invoices
  const invoicesContent = (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-8">
      <InvoicesPage />
    </div>
  );

  // Tab content: Alerts
  const alertsContent = (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-8">
      <AlertsPage />
    </div>
  );

  // Tabs definition
  const billingTabs: TabItem[] = [
    {
      id: 'overview',
      label: 'Visão Geral',
      icon: 'pie-chart',
      content: overviewContent,
    },
    {
      id: 'statement',
      label: 'Extrato',
      icon: 'bar-chart',
      content: statementContent,
    },
    {
      id: 'cards',
      label: 'Cartões',
      icon: 'credit-card',
      content: cardsContent,
    },
    {
      id: 'invoices',
      label: 'Faturas',
      icon: 'file-text',
      content: invoicesContent,
    },
    {
      id: 'alerts',
      label: 'Alertas',
      icon: 'bell',
      content: alertsContent,
    },
  ];

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: 'var(--theme-page-background)' }}
      data-testid="billing-page"
    >
      {/* Sticky DS toolbar — 3 zonas: breadcrumb | centro | acao primaria */}
      <div
        className="sticky top-0 z-10"
        style={{
          backgroundColor: 'var(--theme-header-background)',
          borderBottom: '1px solid var(--theme-header-border)',
        }}
      >
        <div
          className="flex items-center gap-3 px-4 sm:px-6 md:px-8"
          style={{ minHeight: 56 }}
        >
          {/* Left: breadcrumb (nunca seta voltar) */}
          <nav
            className="flex items-center gap-2 min-w-0"
            aria-label="breadcrumb"
            data-testid="billing-breadcrumb"
          >
            <span
              className="text-sm font-medium truncate"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              Configurações
            </span>
            <Icon
              name="chevron-right"
              className="h-3.5 w-3.5 flex-shrink-0"
              style={{ color: 'var(--theme-muted-text)' }}
            />
            <span
              className="text-base font-semibold tracking-tight truncate"
              style={{ color: 'var(--theme-header-text)' }}
            >
              Faturamento
            </span>
          </nav>

          {/* Center spacer (contrato DS 3 zonas) */}
          <div className="flex-1" />

          {/* Right: acao primaria unica */}
          <Button
            icon="check"
            onClick={() => navigate('/billing/plans')}
            className="flex-shrink-0"
            data-testid="billing-change-plan-button"
          >
            Mudar de plano
          </Button>
        </div>
      </div>

      {/* Tabs Navigation + Content */}
      <Tabs
        tabs={billingTabs}
        defaultTab="overview"
        variant="underline"
        pageLevel
      />
    </div>
  );
};

BillingPage.displayName = 'BillingPage';
